import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Loader2, CheckCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import API from '../services/api';

function RecipientConversation() {
  const { id, contactId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConversation();
  }, [id, contactId]);

  const loadConversation = async () => {
    setLoading(true);
    try {
      const res = await API.get(`/campaigns/${id}/recipient/${contactId}/conversation`);
      setData(res.data.data);
    } catch (error) {
      toast.error('Failed to load conversation');
    } finally {
      setLoading(false);
    }
  };

  const getDateLabel = (dateString) => {
    const msgDate = new Date(dateString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    const isSameDay = (d1, d2) =>
      d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
    if (isSameDay(msgDate, today)) return 'Today';
    if (isSameDay(msgDate, yesterday)) return 'Yesterday';
    return msgDate.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const renderMessageContent = (msg) => {
    if (msg.message_type === 'interactive') {
      const parts = msg.content.split('\n[Buttons:');
      const bodyText = parts[0];
      const buttonText = parts[1] ? parts[1].replace(']', '') : '';
      return (
        <div>
          <div className="msg-bubble-text" style={{ whiteSpace: 'pre-wrap' }}>{bodyText}</div>
          {buttonText && (
            <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {buttonText.split('|').map((btn, i) => (
                <span key={i} style={{
                  background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)',
                  borderRadius: '12px', padding: '2px 10px', fontSize: '12px'
                }}>{btn.trim()}</span>
              ))}
            </div>
          )}
        </div>
      );
    }
    return <div className="msg-bubble-text" style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>;
  };

  if (loading) {
    return (
      <div className="loading-container" style={{ padding: '60px 0' }}>
        <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#1e3a5f' }} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="dash-page">
        <button onClick={() => navigate(-1)} className="settings-btn-test" style={{ marginBottom: '14px' }}>
          <ArrowLeft size={15} /> Back
        </button>
        <div className="empty-state">
          <div className="empty-state-icon"><MessageSquare size={56} color="#cbd5e1" /></div>
          <h3 className="empty-state-title">Conversation Not Found</h3>
        </div>
      </div>
    );
  }

  const { contact, messages } = data;

  // Date-wise groups — Messages.js jaisa hi pattern
  const groups = [];
  messages.forEach((msg) => {
    const label = getDateLabel(msg.created_at);
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.label === label) {
      lastGroup.msgs.push(msg);
    } else {
      groups.push({ label, msgs: [msg] });
    }
  });

  return (
    <div className="dash-page">
      <div className="dash-header">
        <div>
          <button onClick={() => navigate(`/bulk-message/campaign/${id}`)} className="settings-btn-test" style={{ marginBottom: '12px' }}>
            <ArrowLeft size={15} /> Back to Campaign
          </button>
          <h1 className="dash-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <MessageSquare size={26} color="#1e3a5f" /> {contact.name || contact.phone}
          </h1>
          <p className="dash-subtitle">{contact.phone}</p>
        </div>
      </div>

      <div className="dash-card">
        {messages.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><MessageSquare size={56} color="#cbd5e1" /></div>
            <h3 className="empty-state-title">No Response Yet</h3>
            <p className="empty-state-description">This recipient hasn't replied to the campaign yet.</p>
          </div>
        ) : (
          <div className="msg-messages-body" style={{ maxHeight: '65vh', overflowY: 'auto', padding: '10px' }}>
            {groups.map((group) => (
              <div key={group.label + group.msgs[0].id}>
                <div style={{ display: 'flex', justifyContent: 'center', margin: '14px 0' }}>
                  <span style={{
                    background: '#e2e8f0', color: '#475569', fontSize: '12px', fontWeight: '600',
                    padding: '4px 14px', borderRadius: '14px'
                  }}>{group.label}</span>
                </div>
                {group.msgs.map((msg) => (
                  <div key={msg.id} className={`msg-bubble-wrap ${msg.direction === 'outgoing' ? 'msg-bubble-wrap-out' : 'msg-bubble-wrap-in'}`}>
                    <div className={`msg-bubble ${msg.direction === 'outgoing' ? 'msg-bubble-out' : 'msg-bubble-in'}`}>
                      {renderMessageContent(msg)}
                      <div className="msg-bubble-meta">
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        {msg.direction === 'outgoing' && <CheckCheck size={14} className="msg-tick" />}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default RecipientConversation;